<script lang="ts" setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
  watch,
} from "vue";
import { useMediaControls } from "@vueuse/core";
import api from "@/modules/api";
import { ChatMessage, TextAudio } from "@/types/entity";
import { useAudio } from "@/stores/audio";
import { showNotify } from "vant";
import axios, { CanceledError, CancelTokenSource } from "axios";
import i18n from "@/locales";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";

interface AudioPlayProps {
  id?: number;
  msg: string;
  mateToneId: number;
  url?: string;
  initialMuted?: boolean;
  iconType?: "play" | "volume" | "wave";
  isBtn?: boolean;
  isChat?: boolean;
  payMode?: "free" | "crystal" | "powerCube";
  amount?: number;
  showRegenerate?: boolean;
  playBtnClass?: string;
  newMateToneId?: number;
  btnClass?: string;
}

const props = withDefaults(defineProps<AudioPlayProps>(), {
  url: "",
  initialMuted: false,
  iconType: "wave",
  isBtn: true,
  isChat: false,
  payMode: "free",
  amount: 0.5,
  showRegenerate: false,
  playBtnClass: "",
  btnClass: "",
});
const emit = defineEmits<{
  (e: "onPlayAudio", ref: HTMLAudioElement): void;
  (e: "onAudioGenerated", textAudio: TextAudio, id?: number): void;
  (e: "onChatAudioGenerated", chatMessage: ChatMessage): void;
}>();

const audioStore = useAudio();
const chatStore = useChat();
const chatSessionStore = useChatSession();
const audioRef = ref<HTMLAudioElement | null>(null);
const isVoiceLoading = ref(false);
const cachedUrl = ref("");

const { playing, currentTime, duration, muted } = useMediaControls(audioRef);
const generatedMsg = ref("");
const shouldGenerate = computed(
  () => !props.url && props.msg !== generatedMsg.value
);
let cancelToken: CancelTokenSource | null = null;
// Lifecycle
onMounted(() => {
  audioStore.addAudioRef(audioRef);
  muted.value = props.initialMuted;
});

onBeforeUnmount(() => {
  stop();
  audioStore.removeAudioRef(audioRef);
});

onActivated(() => {
  audioStore.addAudioRef(audioRef);
});

onDeactivated(() => {
  stop();
  audioStore.removeAudioRef(audioRef);
});
watch(
  () => audioStore.activatedUnMuted,
  (enableAutoPlay) => {
    if (!enableAutoPlay && playing) {
      audioRef.value?.pause();
    }
  }
);
// Methods
const showAudioError = () => {
  showNotify({
    type: "danger",
    message: i18n.global.t("Message.soundPlaybackError"),
  });
  throw new Error("TextAudio.mediaUrl is empty");
};

const playAudio = async (forcePlay = false, forceRegen = false) => {
  audioStore.stopAll();
  if (!audioRef.value) {
    return;
  }
  // 暂停
  if (isVoiceLoading.value) {
    cancelToken?.cancel();
    return;
  }
  if (playing.value) {
    audioRef.value!.pause();
    currentTime.value = 0;
    if (!forcePlay) {
      return;
    }
  }
  if (
    props.isChat &&
    !props.newMateToneId &&
    !props.mateToneId &&
    !forceRegen &&
    !forcePlay
  ) {
    chatSessionStore.selectToneCallBack = () => playAudio(true, true);
    chatSessionStore.openSelectToneModal();
    return;
  } else {
    chatSessionStore.selectToneCallBack = null;
  }

  try {
    if (
      shouldGenerate.value ||
      forceRegen ||
      (props.newMateToneId && props.newMateToneId !== props.mateToneId)
    ) {
      isVoiceLoading.value = true;
      const msg = props.msg;
      const id = props.id;
      if (cancelToken) {
        cancelToken.cancel();
        cancelToken = null;
      }
      cancelToken = axios.CancelToken.source();
      if (props.isChat) {
        const chatMessage = await chatStore.generateChatAudio(
          props.id!,
          cancelToken
        );
        if (!chatMessage.mediaUrl) {
          showAudioError();
        }
        emit("onChatAudioGenerated", chatMessage);
        cachedUrl.value = chatMessage.mediaUrl!;
      } else {
        const res = await api.playTextAudio(
          {
            mateToneId: props.mateToneId,
            text: msg,
          },
          {
            cancelToken: cancelToken.token,
          }
        );
        const textAudio = res.item;
        if (!textAudio) {
          showAudioError();
        }
        emit("onAudioGenerated", res.item, id);
        cachedUrl.value = res.item.mediaUrl;
      }
      generatedMsg.value = msg;
      audioRef.value.src = cachedUrl.value;
      audioRef.value.load();
      isVoiceLoading.value = false;
    } else if (props.url && cachedUrl.value !== props.url) {
      audioRef.value.src = props.url;
      cachedUrl.value = props.url;
      audioRef.value.load();
      isVoiceLoading.value = false;
    }
    try {
      await audioRef.value!.play();
      emit("onPlayAudio", audioRef.value!);
    } catch (e) {
      if (e instanceof DOMException) {
        if (e.name === "NotAllowedError") {
          audioStore.askAutoPlay(audioRef.value);
        }
      }
    }
  } catch (e) {
    if (!(e instanceof CanceledError)) {
      throw e;
    }
  } finally {
    isVoiceLoading.value = false;
  }
};

const stop = () => {
  if (!audioRef.value) {
    return;
  }
  if (cancelToken) {
    cancelToken.cancel();
    cancelToken = null;
  }
  audioRef.value?.pause();
  currentTime.value = 0;
};

export interface AudioPlayInstance {
  playAudio: typeof playAudio;
  stop: typeof stop;
  muted: typeof muted;
}

defineExpose({
  isVoiceLoading,
  playAudio,
  stop,
  muted,
});
</script>
<template>
  <div class="join">
    <div
      v-if="mateToneId > 0 || isChat || url"
      class="btn-xs audio-btn join-item"
      :class="[playing && 'playing', isBtn && 'btn', playBtnClass, btnClass]"
      @click.stop="playAudio()"
    >
      <span v-if="isVoiceLoading" class="loading loading-dots loading-xs" />
      <div v-else class="flex items-center gap-2">
        <i
          v-if="!playing"
          :class="{
            'icon-volume': iconType === 'volume',
            'icon-play1': iconType === 'play',
            'icon-sound-wavex': iconType === 'wave',
          }"
          class="iconfont text-xs"
        />
        <audio-playing-icon v-else class="text-primary-content" />
        <template v-if="duration">
          <span v-if="playing" class="text-xs text-primary-content">
            {{ Math.floor(duration - currentTime) }}″
          </span>
          <span v-else-if="!isVoiceLoading" class="text-xs">
            {{ Math.floor(duration) }}″
          </span>
        </template>
        <span v-else>
          {{ $t("Buttons.voice") }}
        </span>
      </div>
      <audio ref="audioRef" class="hidden" crossorigin="anonymous" />
    </div>
    <button
      v-if="showRegenerate"
      :disabled="isVoiceLoading"
      class="btn btn-xs join-item"
      @click.stop="playAudio(false, true)"
    >
      <i class="iconfont icon-refresh text-xs" />
    </button>
  </div>
</template>
<style>
.audio-btn.playing {
  @apply btn-primary;
}
</style>
